import React, { useState, useEffect, useCallback } from 'react';
import { Activity, CheckCircle, XCircle, Clock, TrendingUp, Server, Globe } from 'lucide-react';

const SERVICES = [
  { name: "Главный сайт", desc: "Мой основной сайт", display: "timka20.ru", check: "https://timka20.ru", category: "Основные" },
  { name: "Главный сайт 2", desc: "Мой основной сайт 2", display: "mv07.ru", check: "https://mv07.ru", category: "Основные" },
  { name: "Главный сервер", desc: "Сервер на котором работает Uploader и часть других сервисов", display: "server.timka20.ru", check: "https://server.timka20.ru", category: "Инфраструктура" },
  { name: "Cloud Storage", desc: "Сервер для хранения файлов", display: "upload.timka20.ru", check: "https://upload.timka20.ru", category: "Инфраструктура" },
  { name: "Сайт RentikAI", desc: "Сайт нейросети RentikAI", display: "rentik.timka20.ru", check: "https://rentik.timka20.ru", category: "AI Сервисы" },
  { name: "API RentikAI", desc: "Единственный endpoint для всех запросов к API RentikAI", display: "ai.timka20.ru", check: "https://ai.timka20.ru", category: "AI Сервисы" },
  { name: "Сайт Boulty", desc: "Сайт нейросети Boutly AI", display: "boutly.timka20.ru", check: "https://boutly.timka20.ru", category: "AI Сервисы" },
  { name: "[1] VPN клиентский адрес", desc: "По данному адресу подключаются клиенты первого сервера", display: "vpn.timka20.ru", check: "http://vpn.timka20.ru", category: "VPN" },
  { name: "[1] Proxy сервер", desc: "Он нужен, чтобы проксировать трафик между клиентом и сервером", display: "vpn.server.timka20.ru", check: "http://vpn.server.timka20.ru", category: "VPN" },
  { name: "[2] VPN клиентский адрес", desc: "По данному адресу подключаются клиенты второго сервера", display: "vpn-1.timka20.ru", check: "https://vpn-1.timka20.ru", category: "VPN" },
  { name: "[2] Proxy сервер", desc: "Он нужен, чтобы проксировать трафик между клиентом и сервером", display: "vpn-1.server.timka20.ru", check: "https://vpn-1.server.timka20.ru", category: "VPN" }
];

export default function App() {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('Все');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  const categories = ['Все', ...new Set(SERVICES.map(s => s.category))];

  const loadFromFile = useCallback(async () => {
    try {
      const response = await fetch('/status.json');
      if (response.ok) {
        const data = await response.json();
        if (data.services && data.stats) {
          setServices(data.services);
          setStats(data.stats);
          setLastUpdate(new Date(data.lastUpdate));
          return true;
        }
      }
    } catch (error) {
      console.log('Файл status.json не найден, будет создан новый');
    }
    return false;
  }, []);

  const saveToFile = useCallback((servicesData, statsData) => {
    const dataToSave = {
      services: servicesData,
      stats: statsData,
      lastUpdate: new Date().toISOString()
    };
    console.log('Данные сохранены в status.json');
  }, []);

  const checkService = async (service) => {
    const startTime = Date.now();
    try {
      const response = await fetch(service.check, { 
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      const responseTime = Date.now() - startTime;
      
      return {
        ...service,
        status: 'online',
        statusCode: response.status || 200,
        responseTime,
        lastCheck: new Date().toISOString(),
        message: 'Сервис работает'
      };
    } catch (error) {
      const responseTime = Date.now() - startTime;
      return {
        ...service,
        status: 'offline',
        statusCode: 0,
        responseTime,
        lastCheck: new Date().toISOString(),
        message: error.message || 'Недоступен'
      };
    }
  };

  const updateStats = useCallback((servicesData, currentStats) => {
    const newStats = {};
    servicesData.forEach(service => {
      const key = service.display;
      if (!currentStats[key]) {
        newStats[key] = {
          checks: [],
          uptime: 100,
          avgResponseTime: 0
        };
      } else {
        newStats[key] = { ...currentStats[key] };
      }

      newStats[key].checks.push({
        timestamp: service.lastCheck,
        status: service.status,
        responseTime: service.responseTime,
        statusCode: service.statusCode
      });

      if (newStats[key].checks.length > 100) {
        newStats[key].checks = newStats[key].checks.slice(-100);
      }

      const totalChecks = newStats[key].checks.length;
      const successfulChecks = newStats[key].checks.filter(c => c.status === 'online').length;
      newStats[key].uptime = ((successfulChecks / totalChecks) * 100).toFixed(2);
      
      const avgTime = newStats[key].checks.reduce((acc, c) => acc + c.responseTime, 0) / totalChecks;
      newStats[key].avgResponseTime = Math.round(avgTime);
    });
    
    return newStats;
  }, []);

  const checkAllServices = useCallback(async () => {
    setIsChecking(true);
    const results = await Promise.all(SERVICES.map(checkService));
    const newStats = updateStats(results, stats);
    
    setServices(results);
    setStats(newStats);
    setLastUpdate(new Date());
    
    saveToFile(results, newStats);
    
    setIsChecking(false);
  }, [stats, updateStats, saveToFile]);

  useEffect(() => {
    const initData = async () => {
      const loaded = await loadFromFile();
      if (!loaded) {
        checkAllServices();
      }
    };
    
    initData();
    
    const interval = setInterval(checkAllServices, 60000);
    return () => clearInterval(interval);
  }, [loadFromFile, checkAllServices]);

  const filteredServices = selectedCategory === 'Все' 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  const getStatusColor = (status, code) => {
    if (status === 'offline') return 'bg-red-500';
    if (code >= 500) return 'bg-red-500';
    if (code >= 400) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  const getUptimeColor = (uptime) => {
    if (uptime >= 99) return 'text-emerald-400';
    if (uptime >= 95) return 'text-yellow-400';
    return 'text-red-400';
  };

  const overallStats = {
    total: services.length,
    online: services.filter(s => s.status === 'online').length,
    offline: services.filter(s => s.status === 'offline').length,
    avgUptime: services.length > 0 
      ? (services.reduce((acc, s) => acc + (parseFloat(stats[s.display]?.uptime) || 100), 0) / services.length).toFixed(2)
      : 100
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[150px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                Мониторинг <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">сервисов</span>.
              </h1>
              <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                Отслеживание статуса всех моих проектов в режиме реального времени
              </p>
            </div>

            {/* Update Info & Button */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-16">
              {lastUpdate && (
                <div className="px-5 py-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  Обновлено: {lastUpdate.toLocaleTimeString('ru-RU')}
                </div>
              )}
              <button 
                onClick={checkAllServices}
                disabled={isChecking}
                className="px-6 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isChecking ? 'Проверка...' : 'Обновить'}
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Server className="w-10 h-10 text-cyan-400/50" />
                  <p className="text-4xl font-bold">{overallStats.total}</p>
                </div>
                <p className="text-gray-400">Всего сервисов</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <CheckCircle className="w-10 h-10 text-emerald-400/50" />
                  <p className="text-4xl font-bold text-emerald-400">{overallStats.online}</p>
                </div>
                <p className="text-gray-400">Онлайн</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <XCircle className="w-10 h-10 text-red-400/50" />
                  <p className="text-4xl font-bold text-red-400">{overallStats.offline}</p>
                </div>
                <p className="text-gray-400">Оффлайн</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-10 h-10 text-cyan-400/50" />
                  <p className={`text-4xl font-bold ${getUptimeColor(overallStats.avgUptime)}`}>
                    {overallStats.avgUptime}%
                  </p>
                </div>
                <p className="text-gray-400">Средний Uptime</p>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 mb-12 justify-center">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
                    selectedCategory === cat
                      ? 'bg-cyan-500 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredServices.map((service, idx) => {
                const serviceStats = stats[service.display] || { uptime: 100, avgResponseTime: 0, checks: [] };
                const recentChecks = serviceStats.checks.slice(-30);
                
                return (
                  <div 
                    key={idx}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status, service.statusCode)} animate-pulse`} />
                          <h3 className="text-2xl font-bold">
                            {service.name}
                          </h3>
                        </div>
                        <p className="text-gray-400 mb-4 leading-relaxed">{service.desc}</p>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-500" />
                          <span className="text-cyan-400 font-mono text-sm">{service.display}</span>
                        </div>
                      </div>
                      <div className="text-right ml-6">
                        <div className={`text-3xl font-bold ${getUptimeColor(serviceStats.uptime)}`}>
                          {serviceStats.uptime}%
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">uptime</div>
                      </div>
                    </div>

                    {/* Status Info */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-black/20 rounded-xl p-4 border border-white/5">
                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Статус</p>
                        <p className={`text-sm font-bold ${
                          service.status === 'online' ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {service.status === 'online' ? 'Онлайн' : 'Оффлайн'}
                        </p>
                      </div>
                      <div className="bg-black/20 rounded-xl p-4 border border-white/5">
                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Код</p>
                        <p className="text-sm font-bold text-white">{service.statusCode || 'N/A'}</p>
                      </div>
                      <div className="bg-black/20 rounded-xl p-4 border border-white/5">
                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Пинг</p>
                        <p className="text-sm font-bold text-white">{serviceStats.avgResponseTime}ms</p>
                      </div>
                    </div>

                    {/* Mini Chart */}
                    <div className="flex items-end gap-1 h-16 mb-4 bg-black/20 rounded-xl p-3">
                      {recentChecks.map((check, i) => {
                        const height = Math.min(100, (check.responseTime / 1000) * 100);
                        const color = check.status === 'online' ? 'bg-emerald-500' : 'bg-red-500';
                        return (
                          <div 
                            key={i} 
                            className={`flex-1 ${color} rounded-t opacity-60 hover:opacity-100 transition-all`}
                            style={{ height: `${height}%` }}
                            title={`${check.responseTime}ms - ${check.status}`}
                          />
                        );
                      })}
                    </div>
                    
                    <div className="text-xs text-gray-500 flex items-center justify-between">
                      <span>Последние 30 проверок</span>
                      <span>{new Date(service.lastCheck).toLocaleString('ru-RU')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-12 mt-20">
          <div className="container mx-auto px-4 text-center">
            <p className="text-gray-400">© 2024-2026 timka20. Система мониторинга сервисов v1.0</p>
            <p className="text-gray-600 text-xs mt-2">Данные сохраняются в файл status.json автоматически</p>
          </div>
        </footer>
      </div>
    </div>
  );
}